import { useState, useEffect } from "react";
import ComposePanel from "./ComposePanel";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES, STAFF } from "../../config/constants";
import useMobile from "../../hooks/useMobile";
import { msCall } from "../../utils/api";
import { btnP, btnS } from "../../utils/styles";
import { sanitizeEmailHtml, looksLikeHtml } from "../../utils/safeHtml";

export default function OutlookPage({ clients, theme, mobile, authUser }) {
  const t = THEMES[theme];
  const _isMobile = useMobile();
  const isMobile = mobile || _isMobile;

  // inbox tab: "focused" | "other"
  const [inboxTab,       setInboxTab]       = useState("focused");
  // filter: "all" | "unread" | "clients"
  const [filterTab,      setFilterTab]      = useState("all");
  const [search,         setSearch]         = useState("");
  const [selectedEmail,  setSelectedEmail]  = useState(null);
  const [compose,        setCompose]        = useState(false);
  const [msStatus,       setMsStatus]       = useState(null);

  // Both tabs populated from a single API call
  const [focusedEmails,  setFocusedEmails]  = useState([]);
  const [otherEmails,    setOtherEmails]    = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [threadCache,    setThreadCache]    = useState({});  // conversationId → messages[]
  const [threadLoading,  setThreadLoading]  = useState(false);

  const [sendLoading,    setSendLoading]    = useState(false);
  const [sendError,      setSendError]      = useState("");
  const [connectLoading, setConnectLoading] = useState(false);
  const staffName = STAFF.find(s => authUser?.email?.toLowerCase().includes(s.toLowerCase())) || "Bibek";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("ms") === "connected") window.history.replaceState({}, "", window.location.pathname);
    checkMsStatus();
  }, []);

  async function checkMsStatus() {
    try {
      const data = await msCall("msGetStatus", {}, "GET");
      setMsStatus(data.connected ? data : false);
      if (data.connected) fetchEmails();
    } catch { setMsStatus(false); }
  }

  async function fetchEmails() {
    setLoading(true);
    try {
      // Single call returns both tabs already split
      const data = await msCall("msFetchEmails", { top: 100 }, "GET");
      setFocusedEmails((data.focused || []).map(e => normalizeEmail(e)));
      setOtherEmails(  (data.other   || []).map(e => normalizeEmail(e)));
    } catch (e) { console.error("fetchEmails:", e.message); }
    finally { setLoading(false); }
  }

  async function fetchThread(email) {
    if (!email || !msStatus) return;
    const cid = email.conversationId;
    if (!cid || threadCache[cid]) return; // already cached
    setThreadLoading(true);
    try {
      const data = await msCall("msFetchThread", { conversationId: cid }, "GET");
      if (data.messages) {
        setThreadCache(prev => ({ ...prev, [cid]: data.messages }));
      }
    } catch (e) { console.error("fetchThread:", e.message); }
    finally { setThreadLoading(false); }
  }

  function normalizeEmail(e) {
    const from = e.from?.emailAddress;
    const clientMatch = clients.find(c => c.email && from?.address &&
      c.email.toLowerCase() === from.address.toLowerCase())?.name || null;
    return {
      id:             e.id,
      conversationId: e.conversationId,
      from:           from?.address || "",
      fromName:       from?.name || from?.address || "Unknown",
      subject:        e.subject || "(no subject)",
      preview:        e.bodyPreview || "",
      date:           e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleDateString() : "",
      time:           e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "",
      read:           e.isRead,
      clientMatch,
      thread:         e.threadCount || 1,
      messages:       (e.messages || []).map(m => ({
        from:     m.from?.emailAddress?.address || "",
        fromName: m.from?.emailAddress?.name || m.from?.emailAddress?.address || "Unknown",
        date:     m.receivedDateTime ? new Date(m.receivedDateTime).toLocaleString([], {month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "",
        body:     m.body || m.bodyPreview || "",
        bodyType: m.bodyType || "text",
        preview:  m.bodyPreview || m.preview || "",
      })),
    };
  }

  async function handleConnect() {
    setConnectLoading(true);
    try {
      const data = await msCall("msGetAuthUrl", {}, "GET");
      window.location.href = data.url;
    } catch (e) { alert("Error: " + e.message); setConnectLoading(false); }
  }

  async function handleDisconnect() {
    if (!window.confirm("Disconnect Microsoft account?")) return;
    await msCall("msDisconnect", {});
    setMsStatus(false);
    setFocusedEmails([]); setOtherEmails([]);
    setSelectedEmail(null);
  }

  async function handleSend({ to, cc, bcc, subject, body }) {
    setSendLoading(true); setSendError("");
    try {
      await msCall("msSendEmail", { to, cc: cc||undefined, bcc: bcc||undefined, subject, body });
      setCompose(false);
    } catch (e) { setSendError(e.message); }
    finally { setSendLoading(false); }
  }

  const activeEmails = msStatus
    ? (inboxTab === "focused" ? focusedEmails : otherEmails)
    : [];

  const unreadCount = activeEmails.filter(e => !e.read).length;
  const clientCount = activeEmails.filter(e => e.clientMatch).length;

  const filtered = activeEmails.filter(e => {
    const ms = !search || e.subject.toLowerCase().includes(search.toLowerCase())
                       || e.fromName.toLowerCase().includes(search.toLowerCase())
                       || e.preview.toLowerCase().includes(search.toLowerCase());
    const tab = filterTab==="all" || (filterTab==="unread" && !e.read) || (filterTab==="clients" && e.clientMatch);
    return ms && tab;
  });

  const selected = selectedEmail ? (activeEmails.find(e => e.id === selectedEmail) || null) : null;
  const thread = selected
    ? (threadCache[selected.conversationId]
        || selected.messages
        || [{ from:selected.from, fromName:selected.fromName, date:`${selected.date} · ${selected.time}`, body:selected.preview+"..." }])
    : [];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:0,height:"calc(100vh - 120px)",minHeight:500}}>

      {/* Top bar */}
      <div style={{background:t.surface,border:`1px solid #0078d430`,borderRadius:16,padding:"14px 20px",marginBottom:12,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div style={{width:38,height:38,borderRadius:10,background:"#0078d415",border:"2px solid #0078d440",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <span style={{fontSize:20}}>📧</span>
        </div>
        <div style={{flex:1,minWidth:160}}>
          <div style={{fontSize:13,fontWeight:800,color:t.text}}>Microsoft Outlook</div>
          {msStatus
            ? <div style={{fontSize:11,color:t.textMuted}}>Connected as <strong>{msStatus.email}</strong></div>
            : <div style={{fontSize:11,color:t.textMuted}}>Connect your Microsoft account to sync inbox</div>
          }
        </div>
        {msStatus && [
          {l:"Loaded", v:activeEmails.length, c:BRAND.blue},
          {l:"Unread", v:unreadCount, c:"#f59e0b"},
          {l:"Clients", v:clientCount, c:BRAND.green},
        ].map(s=>(
          <div key={s.l} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:20,background:s.c+"15",border:`1px solid ${s.c}30`}}>
            <span style={{fontSize:13,fontWeight:800,color:s.c}}>{s.v}</span>
            <span style={{fontSize:10,color:s.c,fontWeight:600,textTransform:"uppercase"}}>{s.l}</span>
          </div>
        ))}
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {msStatus ? (
            <>
              <div style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,background:"#8DC63F18",border:"1px solid #8DC63F40",fontSize:11,color:BRAND.green,fontWeight:600}}>✓ Connected</div>
              <button onClick={()=>fetchEmails()} disabled={loading} style={{...btnS(t),fontSize:11,padding:"5px 10px"}}>{loading?<><MiniSpinner size={12} color={t.text}/>Loading...</>:"↻ Refresh"}</button>
              <button onClick={handleDisconnect} style={{padding:"5px 10px",borderRadius:8,background:"transparent",border:"1px solid #ef444440",color:"#ef4444",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Disconnect</button>
            </>
          ) : (
            <>
              <div style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,background:"#f59e0b18",border:"1px solid #f59e0b40",fontSize:11,color:"#f59e0b",fontWeight:600}}>⚠ Not Connected</div>
              <button onClick={handleConnect} disabled={connectLoading} style={{...btnP,background:"linear-gradient(135deg,#0078d4,#106ebe)",fontSize:12,padding:"6px 14px",display:"flex",alignItems:"center",gap:5}}>
                {connectLoading?<><MiniSpinner size={13} color="#fff"/>Connecting...</>:<>🔗 Connect Microsoft</>}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Preview banner */}
      {!msStatus && (
        <div style={{background:theme==="dark"?"#1a1200":"#fffbeb",border:"1px solid #f59e0b40",borderRadius:10,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#92400e"}}>
          <span>🚧</span><span><strong>Preview UI</strong> — Connect Microsoft to load your real inbox.</span>
        </div>
      )}

      {/* Split pane */}
      <div style={{display:"flex",gap:0,flex:1,minHeight:0,border:`1px solid ${t.border}`,borderRadius:16,overflow:"hidden",background:t.surface}}>

        {/* LEFT — Email list */}
        <div style={{width:isMobile?"100%":340,minWidth:isMobile?0:280,flexShrink:0,borderRight:`1px solid ${t.border}`,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* Focused / Other tabs — Outlook-style */}
          <div style={{display:"flex",borderBottom:`1px solid ${t.border}`,background:t.surface}}>
            {[["focused","Focused"],["other","Other"]].map(([val,label])=>(
              <button key={val} onClick={()=>{setInboxTab(val);setSelectedEmail(null);}}
                style={{flex:1,padding:"11px 0",fontSize:13,fontWeight:inboxTab===val?700:500,cursor:"pointer",border:"none",borderBottom:inboxTab===val?`2px solid ${BRAND.blue}`:"2px solid transparent",background:"transparent",color:inboxTab===val?BRAND.blue:t.textMuted,fontFamily:"'Outfit',sans-serif",transition:"all 0.15s"}}>
                {label}
              </button>
            ))}
          </div>

          {/* Search + compose */}
          <div style={{padding:"10px 10px 8px",borderBottom:`1px solid ${t.border}`,display:"flex",gap:8,alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",background:t.inputBg,border:`1px solid ${t.border}`,borderRadius:8,padding:"6px 10px",gap:6,flex:1}}>
              <Icon name="search" size={13} color={t.textMuted}/>
              <input style={{background:"transparent",border:"none",outline:"none",color:t.text,fontSize:12,width:"100%",fontFamily:"'Outfit',sans-serif"}} placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            <button onClick={()=>setCompose(true)} style={{...btnP,padding:"6px 10px",fontSize:12,display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
              <Icon name="plus" size={12} color="#fff"/>New
            </button>
          </div>

          {/* Filter tabs */}
          <div style={{display:"flex",gap:4,padding:"7px 10px",borderBottom:`1px solid ${t.border}`}}>
            {[["all","All"],["unread","Unread"],["clients","Clients"]].map(([val,label])=>(
              <button key={val} onClick={()=>setFilterTab(val)} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${filterTab===val?BRAND.blue+"60":t.border}`,background:filterTab===val?BRAND.blue+"18":t.bg,color:filterTab===val?BRAND.blue:t.textMuted,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
                {label}{val==="unread"&&unreadCount>0?` (${unreadCount})`:""}
              </button>
            ))}
          </div>

          {/* Email rows */}
          <div style={{flex:1,overflowY:"auto"}}>
            {loading && (
              <div style={{display:"flex",flexDirection:"column",gap:0}}>
                {[...Array(6)].map((_,i)=>(
                  <div key={i} style={{padding:"12px 14px",borderBottom:`1px solid ${t.border}`,display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{width:32,height:32,borderRadius:"50%",background:t.border,flexShrink:0}}/>
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
                      <div style={{height:11,background:t.border,borderRadius:4,width:"60%"}}/>
                      <div style={{height:10,background:t.border,borderRadius:4,width:"80%"}}/>
                      <div style={{height:9,background:t.border,borderRadius:4,width:"45%"}}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && filtered.length===0 && (
              <div style={{textAlign:"center",padding:"40px 20px",color:t.textMuted,fontSize:12}}>
                <div style={{fontSize:24,marginBottom:8}}>📭</div>
                No emails in {inboxTab === "focused" ? "Focused" : "Other"}
              </div>
            )}
            {filtered.map(email=>{
              const isSelected = selectedEmail===email.id;
              return (
                <div key={email.id} onClick={()=>{ const next = isSelected?null:email.id; setSelectedEmail(next); if(next) fetchThread(email); }}
                  style={{padding:"11px 14px",borderBottom:`1px solid ${t.borderLight||t.border}`,cursor:"pointer",background:isSelected?(theme==="dark"?"#0e1a2e":"#e8f4ff"):(!email.read?(theme==="dark"?"#0a1220":"#f5f9ff"):"transparent"),borderLeft:`3px solid ${isSelected?BRAND.blue:(!email.read?BRAND.blue+"80":"transparent")}`,transition:"background 0.1s"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                    <div style={{width:32,height:32,borderRadius:"50%",background:email.clientMatch?BRAND.blue+"20":"#88888818",border:`2px solid ${email.clientMatch?BRAND.blue+"50":"#88888830"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:email.clientMatch?BRAND.blue:t.textMuted,flexShrink:0}}>
                      {(email.fromName||"?")[0]}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:12.5,fontWeight:email.read?500:700,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:140}}>{email.fromName}</span>
                        <span style={{fontSize:10,color:t.textMuted,flexShrink:0,marginLeft:4}}>{email.date}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{paddingLeft:40}}>
                    <div style={{fontSize:12,fontWeight:email.read?500:700,color:email.read?t.textSub:t.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginBottom:2}}>{email.subject}</div>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontSize:11,color:t.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",flex:1}}>{email.preview}</span>
                      {email.thread>1&&<span style={{fontSize:10,color:t.textMuted,background:t.bg,border:`1px solid ${t.border}`,borderRadius:6,padding:"0 5px",flexShrink:0}}>{email.thread}</span>}
                      {email.clientMatch&&<span style={{fontSize:9,fontWeight:700,color:BRAND.green,background:BRAND.green+"18",borderRadius:6,padding:"1px 5px",flexShrink:0}}>👤</span>}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Load More button */}
            
          </div>
        </div>

        {/* RIGHT — Thread view */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>
          {!selected ? (
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,color:t.textMuted}}>
              <span style={{fontSize:40}}>📬</span>
              <div style={{fontSize:14,fontWeight:600}}>Select an email to read</div>
              <div style={{fontSize:12}}>Click any email from the list on the left</div>
            </div>
          ) : (
            <>
              <div style={{padding:"16px 24px",borderBottom:`1px solid ${t.border}`,background:t.surface}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
                  <div style={{fontSize:17,fontWeight:800,color:t.text,lineHeight:1.3}}>{selected.subject}</div>
                  <div style={{display:"flex",gap:8,flexShrink:0}}>
                    <button onClick={()=>setCompose({replyTo:selected})} style={{...btnP,fontSize:12,padding:"6px 14px",display:"flex",alignItems:"center",gap:5}}>
                      <Icon name="arrow" size={12} color="#fff"/>Reply
                    </button>
                    <button onClick={()=>setCompose({replyTo:selected,forward:true})} style={{...btnS(t),fontSize:12,padding:"6px 14px"}}>Forward</button>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  {selected.clientMatch&&<span style={{fontSize:11,fontWeight:700,color:BRAND.green,background:BRAND.green+"18",border:`1px solid ${BRAND.green}30`,borderRadius:8,padding:"2px 8px"}}>👤 {selected.clientMatch}</span>}
                  {selected.thread>1&&<span style={{fontSize:11,color:t.textMuted,background:t.bg,border:`1px solid ${t.border}`,borderRadius:8,padding:"2px 8px"}}>{selected.thread} messages in thread</span>}
                </div>
              </div>
              <div style={{flex:1,overflowY:"auto",padding:"0 0 20px"}}>
                {threadLoading && (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:32,color:t.textMuted}}>
                    <span style={{display:"inline-block",width:18,height:18,border:`2px solid ${BRAND.blue}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
                    Loading thread...
                  </div>
                )}
                {!threadLoading && thread.map((msg,mi)=>(
                  <div key={mi} style={{borderBottom:`1px solid ${t.border}`,padding:"20px 24px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:38,height:38,borderRadius:"50%",background:BRAND.blue+"20",border:`2px solid ${BRAND.blue}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:BRAND.blue,flexShrink:0}}>
                          {(msg.fromName||"?")[0]}
                        </div>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:t.text}}>{msg.fromName}</div>
                          <div style={{fontSize:11,color:t.textMuted}}>{msg.from}</div>
                        </div>
                      </div>
                      <span style={{fontSize:11,color:t.textMuted,flexShrink:0}}>{msg.date}</span>
                    </div>
                    <div style={{fontSize:13,color:t.text,lineHeight:1.7,overflowX:"auto"}}>
                      {msg.bodyType==="html" || looksLikeHtml(msg.body)
                        ? <div style={{maxWidth:"100%",wordBreak:"break-word"}} dangerouslySetInnerHTML={{__html: sanitizeEmailHtml(msg.body)}}/>
                        : <div style={{whiteSpace:"pre-wrap"}}>{msg.body || msg.preview || ""}</div>
                      }
                    </div>
                  </div>
                ))}
              </div>
              <div style={{padding:"12px 24px",borderTop:`1px solid ${t.border}`,background:t.surface}}>
                <button onClick={()=>setCompose({replyTo:selected})} style={{...btnP,fontSize:12,display:"flex",alignItems:"center",gap:6,background:"linear-gradient(135deg,#0078d4,#106ebe)"}}>
                  <Icon name="arrow" size={12} color="#fff"/>Reply to {selected.fromName}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {compose&&(
        <ComposePanel
          onClose={()=>setCompose(false)}
          theme={theme}
          staffName={staffName}
          replyTo={compose.replyTo||null}
          toEmail={compose.replyTo?.from||""}
          toName={compose.replyTo?.fromName||""}
          onSend={msStatus ? handleSend : null}
          sendLoading={sendLoading}
          sendError={sendError}
          msConnected={!!msStatus}
        />
      )}
    </div>
  );
}




// ─── QuickBooks Page ───────────────────────────────────────────────────────────
// ─── OneDrive Page ────────────────────────────────────────────────────────────
const MOCK_FOLDERS = [
  { id:"f1", name:"Bibek Panday Kshetri", modified:"2026-03-08", size:"4 files", type:"folder" },
  { id:"f2", name:"Don Quach",            modified:"2026-03-07", size:"7 files", type:"folder" },
  { id:"f3", name:"Sarah Johnson",        modified:"2026-02-28", size:"2 files", type:"folder" },
  { id:"f4", name:"Ivy Capital LLC",      modified:"2026-03-01", size:"3 files", type:"folder" },
  { id:"f5", name:"Michael Chen",         modified:"2026-02-20", size:"5 files", type:"folder" },
  { id:"f6", name:"Quach Holdings Inc",   modified:"2026-03-10", size:"9 files", type:"folder" },
  { id:"f7", name:"Amanda Torres",        modified:"2026-01-15", size:"1 file",  type:"folder" },
];

const MOCK_FILES = {
  f1:[ { name:"2024_Tax_Return.pdf",   size:"1.2 MB", modified:"2026-03-08", icon:"📄" },
       { name:"W2_2024.pdf",           size:"320 KB", modified:"2026-02-14", icon:"📄" },
       { name:"Engagement_Letter.pdf", size:"540 KB", modified:"2026-01-10", icon:"📄" },
       { name:"Notes.docx",           size:"88 KB",  modified:"2026-03-08", icon:"📝" } ],
  f2:[ { name:"2024_Tax_Return.pdf",        size:"2.1 MB", modified:"2026-03-07", icon:"📄" },
       { name:"Engagement_Letter_2025.pdf", size:"540 KB", modified:"2026-01-05", icon:"📄" },
       { name:"QuachHoldings_K1.pdf",       size:"780 KB", modified:"2026-03-01", icon:"📄" },
       { name:"Bank_Statements_Q4.pdf",     size:"3.4 MB", modified:"2026-02-20", icon:"📄" },
       { name:"Expenses_2024.xlsx",         size:"210 KB", modified:"2026-02-18", icon:"📊" },
       { name:"Correspondence.docx",        size:"95 KB",  modified:"2026-03-07", icon:"📝" },
       { name:"Photo_ID.jpg",              size:"1.8 MB", modified:"2025-12-01", icon:"🖼" } ],
  f3:[ { name:"Engagement_Letter.pdf", size:"540 KB", modified:"2026-02-28", icon:"📄" },
       { name:"W2_2024.pdf",           size:"310 KB", modified:"2026-02-10", icon:"📄" } ],
  f4:[ { name:"Corp_Return_2024.pdf",    size:"1.9 MB", modified:"2026-03-01", icon:"📄" },
       { name:"Bookkeeping_2024.xlsx",   size:"450 KB", modified:"2026-02-25", icon:"📊" },
       { name:"Balance_Sheet_Q4.xlsx",   size:"320 KB", modified:"2026-02-20", icon:"📊" } ],
  f5:[ { name:"2024_Tax_Return.pdf",   size:"980 KB", modified:"2026-02-20", icon:"📄" },
       { name:"1099_Forms.pdf",        size:"220 KB", modified:"2026-01-30", icon:"📄" },
       { name:"Deductions_List.docx",  size:"75 KB",  modified:"2026-02-15", icon:"📝" },
       { name:"Investment_Summary.xlsx",size:"180 KB",modified:"2026-02-10", icon:"📊" },
       { name:"Signed_Engagement.pdf", size:"540 KB", modified:"2026-01-08", icon:"📄" } ],
  f6:[ { name:"S-Corp_Return_2024.pdf",    size:"2.3 MB", modified:"2026-03-10", icon:"📄" },
       { name:"Payroll_Summary_2024.xlsx", size:"390 KB", modified:"2026-03-05", icon:"📊" },
       { name:"Bookkeeping_YE_2024.xlsx",  size:"520 KB", modified:"2026-03-08", icon:"📊" },
       { name:"Bank_Recon_Dec2024.xlsx",   size:"140 KB", modified:"2026-03-01", icon:"📊" },
       { name:"Engagement_Letter.pdf",     size:"540 KB", modified:"2026-01-06", icon:"📄" },
       { name:"Articles_of_Incorporation.pdf",size:"1.1 MB",modified:"2025-06-10",icon:"📄" },
       { name:"EIN_Letter.pdf",            size:"210 KB", modified:"2025-06-10", icon:"📄" },
       { name:"Operating_Agreement.pdf",   size:"890 KB", modified:"2025-06-10", icon:"📄" },
       { name:"2023_Tax_Return.pdf",       size:"2.0 MB", modified:"2025-11-15", icon:"📄" } ],
  f7:[ { name:"Engagement_Letter.pdf",size:"540 KB",modified:"2026-01-15",icon:"📄" } ],
};

